import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { showTemporaryMessage } from './Message';
import { get_emergency_contacts, create_emergency_contact, update_emergency_contact, delete_emergency_contact } from '../tools-network';

const EmergencyContacts = ({ loginToken, isEditingContacts, setIsEditingContacts }) => {
	const { t } = useTranslation();
	const [contacts, setContacts] = useState([]);
	const [loading, setLoading] = useState(true);
	const [editingId, setEditingId] = useState(null);
	const [showAddForm, setShowAddForm] = useState(false);
	const editMode = isEditingContacts || false;
	
	// Phone validation function
	const isValidPhone = (phone) => {
		if (!phone || !phone.trim()) return false;
		const cleaned = phone.replace(/\s+/g, '');
		// Swedish mobile: 07X XXXXXXX (10 digits) or +467XXXXXXXX (12 chars with +)
		return /^(07\d{8}|\+467\d{8})$/.test(cleaned);
	};
	
	// Form states
	const [newPhone, setNewPhone] = useState('');
	const [newDescription, setNewDescription] = useState('');
	const [editPhone, setEditPhone] = useState('');
	const [editDescription, setEditDescription] = useState('');
	const [originalPhone, setOriginalPhone] = useState('');
	const [originalDescription, setOriginalDescription] = useState('');
	
	// Delete confirmation states
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [deleteContactId, setDeleteContactId] = useState(null);
	const [deleteConfirmName, setDeleteConfirmName] = useState('');

	useEffect(() => {
		loadContacts();
	}, [loginToken]);

	const loadContacts = async () => {
		try {
			setLoading(true);
			const res = await get_emergency_contacts(loginToken);
			if (res.success) {
				setContacts(res.data || []);
			} else {
				showTemporaryMessage(t('failed_to_load_emergency_contacts'), res.msg, 'danger');
			}
		} catch (err) {
			showTemporaryMessage(t('error_loading_emergency_contacts'), String(err), 'danger');
		} finally {
			setLoading(false);
		}
	};

	const handleAdd = async () => {
		if (!newPhone.trim()) {
			showTemporaryMessage(t('phone_required'), '', 'danger');
			return;
		}

		try {
			const res = await create_emergency_contact(loginToken, newPhone, newDescription);
			if (res.success) {
				showTemporaryMessage(t('emergency_contact_added'), '', 'success');
				setNewPhone('');
				setNewDescription('');
				setShowAddForm(false);
				loadContacts();
			} else {
				showTemporaryMessage(t('failed_to_add_contact'), res.msg, 'danger');
			}
		} catch (err) {
			showTemporaryMessage(t('error_adding_contact'), String(err), 'danger');
		}
	};

	const handleUpdate = async (contactId) => {
		try {
			const updates = {};
			if (editPhone) updates.phone = editPhone;
			if (editDescription !== undefined) updates.description = editDescription;

			const res = await update_emergency_contact(loginToken, contactId, updates);
			if (res.success) {
				showTemporaryMessage(t('contact_updated'), '', 'success');
				setEditingId(null);
				setEditPhone('');
				setEditDescription('');
				loadContacts();
			} else {
				showTemporaryMessage(t('failed_to_update_contact'), res.msg, 'danger');
			}
		} catch (err) {
			showTemporaryMessage(t('error_updating_contact'), String(err), 'danger');
		}
	};

	const handleDelete = async (contactId) => {
		// Don't allow deleting the primary contact
		const contact = contacts.find(c => c.id === contactId);
		if (contact && contact.is_primary) {
			showTemporaryMessage(t('cannot_delete_primary_contact'), t('set_another_contact_primary_first'), 'danger');
			return;
		}

		// Don't allow deleting if only one contact remains
		if (contacts.length <= 1) {
			showTemporaryMessage(t('cannot_delete_last_contact'), t('must_have_at_least_one_contact'), 'danger');
			return;
		}

		// Show confirmation modal
		setDeleteContactId(contactId);
		setShowDeleteConfirm(true);
	};

	const confirmDeleteContact = async () => {
		const contact = contacts.find(c => c.id === deleteContactId);
		const expectedName = contact?.description || contact?.phone || '';
		
		if (deleteConfirmName !== expectedName) {
			showTemporaryMessage(t('name_does_not_match'), '', 'danger');
			return;
		}

		try {
			const res = await delete_emergency_contact(loginToken, deleteContactId);
			if (res.success) {
				showTemporaryMessage(t('contact_deleted'), '', 'success');
				setShowDeleteConfirm(false);
				setDeleteContactId(null);
				setDeleteConfirmName('');
				loadContacts();
			} else {
				showTemporaryMessage(t('failed_to_delete_contact'), res.msg, 'danger');
			}
		} catch (err) {
			showTemporaryMessage(t('error_deleting_contact'), String(err), 'danger');
		}
	};

	const handleSetPrimary = async (contactId) => {
		try {
			const res = await update_emergency_contact(loginToken, contactId, { is_primary: true });
			if (res.success) {
				showTemporaryMessage(t('primary_contact_updated'), '', 'success');
				loadContacts();
			} else {
				showTemporaryMessage(t('failed_to_set_primary'), res.msg, 'danger');
			}
		} catch (err) {
			showTemporaryMessage(t('error_setting_primary'), String(err), 'danger');
		}
	};

	const startEdit = (contact) => {
		setEditingId(contact.id);
		setEditPhone(contact.phone);
		setEditDescription(contact.description);
		setOriginalPhone(contact.phone);
		setOriginalDescription(contact.description);
	};

	const cancelEdit = () => {
		setEditingId(null);
		setEditPhone('');
		setEditDescription('');
		setOriginalPhone('');
		setOriginalDescription('');
	};

	if (loading) {
		return <div className="p-4">{t('loading_emergency_contacts')}</div>;
	}

	return (
		<div className="w-full bg-white rounded-lg shadow p-6">
			<div className="mb-4">
				<h2 className="text-2xl font-bold mb-3">{t('emergency_contacts')}</h2>
				<div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
					<div className="flex items-start">
						<svg className="w-5 h-5 text-blue-500 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
							<path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
						</svg>
						<div className="text-sm text-gray-700">
							<p className="font-semibold mb-1">{t('about_primary_contact')}</p>
							<p>{t('primary_contact_info')}</p>
						</div>
					</div>
				</div>
				<p className="text-gray-600">
					{t('all_contacts_receive_sms')}
				</p>
			</div>

			{/* Add new contact form */}
			{showAddForm && (
				<div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
					<h3 className="font-semibold mb-3">{t('add_new_emergency_contact')}</h3>
					<div className="space-y-3">
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								{t('description')}
							</label>
							<input
								type="text"
								value={newDescription}
								onChange={(e) => setNewDescription(e.target.value)}
								placeholder={t('description_placeholder')}
								className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-cyan-500 focus:border-cyan-500"
							/>
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								{t('phone_number')} *
							</label>
							<input
								type="text"
								value={newPhone}
								onChange={(e) => setNewPhone(e.target.value)}
								placeholder="070 123 45 67"
								className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-cyan-500 focus:border-cyan-500"
							/>
							<p className="text-xs text-gray-500 mt-1">{t('phone_format_help')}</p>
						</div>
						<div className="flex gap-2">
							<button
								onClick={handleAdd}
							disabled={!isValidPhone(newPhone) || !newDescription.trim()}
								className="px-4 py-2 rounded-md transition disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed bg-cyan-500 text-white hover:bg-cyan-600"
							>
								{t('save_contact')}
							</button>
							<button
								onClick={() => {
									setShowAddForm(false);
									setNewPhone('');
									setNewDescription('');
								}}
								className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition"
							>
								{t('cancel')}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Contact list */}
			<div className="space-y-3">
				{contacts.length === 0 ? (
					<p className="text-gray-500 italic">{t('no_emergency_contacts_yet')}</p>
				) : (
				contacts.sort((a, b) => (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0)).map((contact) => (
						<div
							key={contact.id}
							className={`p-4 border rounded-lg ${
								contact.is_primary ? 'border-cyan-500 bg-cyan-50' : 'border-gray-200'
							}`}
						>
							{editingId === contact.id ? (
								// Edit mode
								<div className="space-y-3">
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">
											{t('description')}
										</label>
										<input
											type="text"
											value={editDescription}
											onChange={(e) => setEditDescription(e.target.value)}
											placeholder={t('description_placeholder')}
											className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-cyan-500 focus:border-cyan-500"
										/>
									</div>
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">
											{t('phone_number')}
										</label>
										<input
											type="text"
											value={editPhone}
											onChange={(e) => setEditPhone(e.target.value)}
											className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-cyan-500 focus:border-cyan-500"
										/>
									</div>
									<div className="flex gap-2">
										<button
											onClick={() => handleUpdate(contact.id)}
											disabled={editPhone === originalPhone && editDescription === originalDescription}
											className="px-3 py-1 rounded text-sm transition disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed bg-blue-500 text-white hover:bg-blue-600"
										>
											{t('save_changes')}
										</button>
										<button
											onClick={cancelEdit}
											className="bg-gray-500 text-white px-3 py-1 rounded text-sm hover:bg-gray-600 transition"
										>
											{t('cancel')}
										</button>
									</div>
								</div>
							) : (
								// View mode
								<div>
									<div className="flex justify-between items-start">
										<div className="flex-grow">
											<div className="flex items-center gap-2 mb-1">
												{contact.description && (
													<span className="font-semibold text-lg">{contact.description}</span>
												)}
												{contact.is_primary && (
													<span className="bg-cyan-500 text-white text-xs px-2 py-1 rounded">
													{t('primary')}
													</span>
												)}
											</div>
											<p className="text-gray-600">{contact.phone}</p>
										</div>
									</div>
									{editMode && (
										<div className="flex gap-2 mt-3">
											<button
												onClick={() => startEdit(contact)}
												disabled={showAddForm}
												className="bg-cyan-500 text-white px-3 py-1.5 rounded text-sm font-medium hover:bg-cyan-600 transition disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
											>
											{t('edit')}
											</button>
											{!contact.is_primary && (
												<>
													<button
														onClick={() => handleSetPrimary(contact.id)}
														className="bg-blue-500 text-white px-3 py-1.5 rounded text-sm font-medium hover:bg-blue-600 transition"
													>
													{t('set_as_primary')}
													</button>
													<button
														onClick={() => handleDelete(contact.id)}
														className="bg-red-500 text-white px-3 py-1.5 rounded text-sm font-medium hover:bg-red-600 transition"
													>
													{t('delete_contact')}
													</button>
												</>
											)}
										</div>
									)}
								</div>
							)}
						</div>
					))
				)}
			</div>

			{/* Edit Contacts and Add Contact buttons below list */}
			<div className="mt-4 flex gap-2">
				{!editMode && !showAddForm && (
					<button
						onClick={() => setIsEditingContacts(true)}
						className="bg-cyan-500 text-white px-4 py-2 rounded-md hover:bg-cyan-600 transition"
					>
						{t('edit_contacts')}
					</button>
				)}
				{editMode && !showAddForm && (
					<button
						onClick={() => setIsEditingContacts(false)}
						disabled={editingId !== null}
						className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
					>
						{t('done_editing')}
					</button>
				)}
				{contacts.length < 3 && !showAddForm && editMode && (
					<button
						onClick={() => setShowAddForm(true)}
						disabled={editingId !== null}
						className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
					>
						{t('add_contact')}
					</button>
				)}
			</div>

			{contacts.length >= 3 && (
				<p className="text-gray-500 text-sm mt-4 italic">
					{t('max_contacts_reached')}
				</p>
			)}

			{/* DELETE CONFIRMATION MODAL */}
			{showDeleteConfirm && (() => {
				const contact = contacts.find(c => c.id === deleteContactId);
				const displayName = contact?.description || contact?.phone || '';
				return (
					<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
						<div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
							<h3 className="text-lg font-bold text-gray-900 mb-4">{t('confirm_delete_contact')}</h3>
							<div className="bg-red-50 border-l-4 border-red-600 p-4 mb-4">
								<div className="flex">
									<div>
										<p className="text-sm font-semibold text-red-800">{t('delete_contact_warning_title')}</p>
										<p className="text-sm text-red-700 mt-1">{t('delete_contact_warning_message')}</p>
									</div>
								</div>
							</div>
							<p className="text-sm text-gray-700 mb-4">
								{t('type_contact_name_to_confirm')}: <span className="font-bold">{displayName}</span>
							</p>
							<input
								type="text"
								value={deleteConfirmName}
								onChange={(e) => setDeleteConfirmName(e.target.value)}
								placeholder={t('enter_contact_name')}
								className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-cyan-600"
							/>
							<div className="flex gap-2 justify-end">
								<button
									onClick={() => {
										setShowDeleteConfirm(false);
										setDeleteContactId(null);
										setDeleteConfirmName('');
									}}
									className="bg-gray-500 text-white px-4 py-2 rounded-md text-sm hover:bg-gray-600"
								>
									{t('cancel')}
								</button>
								<button
									onClick={confirmDeleteContact}
									disabled={deleteConfirmName !== displayName}
									className="bg-red-600 text-white px-4 py-2 rounded-md text-sm hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
								>
									{t('delete_contact')}
								</button>
							</div>
						</div>
					</div>
				);
			})()}
		</div>
	);
};

export default EmergencyContacts;
